import { useCallback } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { Mic, MicOff, Camera, VideoOff, MonitorUp, MonitorOff } from 'lucide-react';
import useVideoStore from '../../stores/videoStore';
import { useTranslation } from '../../i18n';

/**
 * Video control buttons for mute/unmute, camera toggle, screen share.
 * Source of truth = reactive isCameraEnabled / isMicrophoneEnabled from
 * useLocalParticipant(). The hook subscribes to TrackMuted/Unmuted,
 * TrackPublished/Unpublished, LocalTrackPublished/Unpublished,
 * MediaDevicesError, ParticipantPermissionsChanged — covering every
 * transition that can change the published track state, so the icon
 * stays in sync with what remote peers actually see.
 * Must be used inside a LiveKitRoom context.
 */
const VideoControls = ({ showScreenShare = true, className = '' }) => {
  const { t } = useTranslation();
  const {
    localParticipant,
    isCameraEnabled,
    isMicrophoneEnabled,
    isScreenShareEnabled,
  } = useLocalParticipant();
  const setScreenSharing = useVideoStore(s => s.setScreenSharing);

  const toggleMicrophone = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
    } catch (error) {
      console.error('Error toggling microphone:', error);
    }
  }, [localParticipant, isMicrophoneEnabled]);

  const toggleCamera = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setCameraEnabled(!isCameraEnabled);
    } catch (error) {
      console.error('Error toggling camera:', error);
    }
  }, [localParticipant, isCameraEnabled]);

  const toggleScreenShare = useCallback(async () => {
    if (!localParticipant) return;
    try {
      const next = !isScreenShareEnabled;
      await localParticipant.setScreenShareEnabled(next);
      setScreenSharing(next);
    } catch (error) {
      console.error('Error toggling screen share:', error);
      // User might have cancelled the screen share dialog
      setScreenSharing(false);
    }
  }, [localParticipant, isScreenShareEnabled, setScreenSharing]);

  return (
    <div className={`flex flex-col md:flex-row items-center gap-2 ${className}`}>
      {/* Microphone toggle */}
      <button
        onClick={toggleMicrophone}
        className={`p-2 rounded-lg transition ${
          isMicrophoneEnabled
            ? 'text-white/80 hover:text-white hover:bg-white/10'
            : 'bg-red-500 text-white hover:bg-red-600'
        }`}
        title={isMicrophoneEnabled ? t('video.muteMic') : t('video.unmuteMic')}
      >
        {isMicrophoneEnabled ? <Mic size={20} /> : <MicOff size={20} />}
      </button>

      {/* Camera toggle */}
      <button
        onClick={toggleCamera}
        className={`p-2 rounded-lg transition ${
          isCameraEnabled
            ? 'text-white/80 hover:text-white hover:bg-white/10'
            : 'bg-red-500 text-white hover:bg-red-600'
        }`}
        title={isCameraEnabled ? t('video.muteCamera') : t('video.unmuteCamera')}
      >
        {isCameraEnabled ? <Camera size={20} /> : <VideoOff size={20} />}
      </button>

      {/* Screen share toggle (optional) */}
      {showScreenShare && (
        <button
          onClick={toggleScreenShare}
          className={`p-2 rounded-lg transition ${
            isScreenShareEnabled
              ? 'bg-brand-primary text-white hover:bg-brand-primary/90'
              : 'text-white/80 hover:text-white hover:bg-white/10'
          }`}
          title={isScreenShareEnabled ? t('video.stopScreenShare') : t('video.startScreenShare')}
        >
          {isScreenShareEnabled ? <MonitorOff size={20} /> : <MonitorUp size={20} />}
        </button>
      )}
    </div>
  );
};

export default VideoControls;
